import React, { useState, useRef, useEffect } from "react";
import {
  Form,
  Input,
  Select,
  Button,
  Switch,
  Modal,
  notification,
  Checkbox,
} from "antd";
import { CloseOutlined, MenuOutlined } from "@ant-design/icons";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { useParams, useNavigate } from "react-router-dom";

const lockVerticalDragStyle = (style) => {
  if (!style?.transform) return style;

  let y = "0px";
  const transform = style.transform;
  const translate3d = transform.match(
    /translate3d\(\s*([^,]+),\s*([^,]+),\s*([^)]+)\)/,
  );
  const translate = transform.match(/translate\(\s*([^,]+),\s*([^)]+)\)/);
  const matrix = transform.match(/matrix\(\s*([^)]+)\)/);

  if (translate3d) {
    y = translate3d[2].trim();
  } else if (translate) {
    y = translate[2].trim();
  } else if (matrix) {
    const parts = matrix[1].split(",").map((part) => part.trim());
    y = `${parts[5] || 0}px`;
  }

  return {
    ...style,
    transform: `translate(0px, ${y})`,
  };
};
import {
  useCreateQuestionMutation,
  useDeleteQuestionMutation,
  useGetFormQuestionsQuery,
  useGetInterpretersQuery,
  useGetOrganizationsQuery,
  useGetQuestionByIdQuery,
  useGetVideoCatalogQuery,
  useUploadFileMutation,
  useUpdateQuestionMutation,
  useUploadVideoMutation,
} from "../../../store/api/apiSlice";
import QuestionPageHeader from "./PageHeader";
import VideoBucketModal from "../../../components/Modals/VideoBucketModal";
import { getQueryParams, languageOptions } from "../../../utils";
import UploadConfirmationModal from "../../../components/Modals/UploadConfirmationModal";
import { Frown, Meh, Smile, SmileIcon, SmilePlus, Star } from "lucide-react";
import {
  getSliderScaleSelectOptions,
  inputTypeOptions,
  dateTypes,
  fileTypes,
  buttonOptions,
  parseSliderOptionLabels,
  joinSliderOptionLabels,
  resolveSliderScaleType,
  getSliderOptionsValidationError,
  isMissingSliderOptionLabel,
  buildChoiceInputResponseFields,
  resolveShowLogosFlag,
  resolveSliderToggleFlags,
  toSliderToggleValue,
  getChoiceLogosValidationError,
  parseChoiceOptionLines,
  isFileUploadInputType,
} from "./constants";
import ChoiceOptionsEditor from "./ChoiceOptionsEditor";
import RadioListEditor from "./RadioListEditor";
import {
  getRadioListSetupError,
  getRadioListEditorState,
  buildRadioListResponseFields,
  getRadioListTypeLabel,
  isRadioListInputType,
} from "../../../utils/radioList";
import {
  HeaderActions,
  QuestionButtonConfig,
  UploadProgressOverlay,
  VideoModal,
} from "./AddQuestionShared";
import {
  buildInputConditionPayload,
  getInputCondition,
  isChoiceInputType,
  normalizeConditionId,
} from "../../../utils/inputConditions";

const MAX_QUESTION_VIDEO_UPLOAD_BYTES = 150 * 1024 * 1024;
const MAX_QUESTION_VIDEO_UPLOAD_MB = 150;

const { Option } = Select;

const AddQuestion = ({ mode = "add" }) => {
  const [mainForm] = Form.useForm();
  const navigate = useNavigate();
  const [deleteQuestion] = useDeleteQuestionMutation();
  const [updateQuestion] = useUpdateQuestionMutation();
  const { formId, questionId } = useParams();
  const { data: orgsData } = useGetOrganizationsQuery();
  const { data: interpretersData } = useGetInterpretersQuery();
  const [uploadModalForm] = Form.useForm();
  const [uploadVideo] = useUploadVideoMutation();
  const [uploadFile] = useUploadFileMutation();

  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [conditionalEnabled, setConditionalEnabled] = useState(false);
  const [useQuestionNumberRedirect, setUseQuestionNumberRedirect] =
    useState(false);
  const [showUploadConfirmation, setShowUploadConfirmation] = useState(false);
  const [pendingCatalogVideo, setPendingCatalogVideo] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [videoFilters, setVideoFilters] = useState({});
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [videoPreview, setVideoPreview] = useState(null);
  const [inputs, setInputs] = useState([
    {
      id: `${Date.now()}`,
      type: "text",
      title: "",
      require: false,
      options: undefined,
      dateType: undefined,
      fileType: undefined,
      recordingLimit: undefined,
    },
  ]);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [isPageReady, setIsPageReady] = useState(false);
  const [videoModalVisible, setVideoModalVisible] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [uploadedVideo, setUploadedVideo] = useState(null);
  const [bucketModalVisible, setBucketModalVisible] = useState(false);

  const videoRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const timerRef = useRef(null);

  const { data: videosData } = useGetVideoCatalogQuery(
    getQueryParams(videoFilters),
  );
  const { data: questionData, isLoading: isQuestionLoading } =
    mode === "edit"
      ? useGetQuestionByIdQuery(questionId, {
          refetchOnMountOrArgChange: true,
        })
      : { data: null, isLoading: false };

  const [createQuestion] = useCreateQuestionMutation();
  const { data: questionsData, isLoading: isQuestionsListLoading } =
    useGetFormQuestionsQuery(formId);
  const existingNumbers = questionsData?.data?.map((q) => q.number);
  const nextNumber =
    existingNumbers?.length > 0 ? Math.max(...existingNumbers) + 1 : 0;

  const questionNumber =
    mode === "edit"
      ? questionData?.data?.number
      : isQuestionsListLoading
        ? undefined
        : nextNumber;
  const canRenderQuestionOptions =
    mode !== "edit" || Boolean(questionData?.data);
  const showFirstQuestionOptions =
    canRenderQuestionOptions && questionNumber === 0;
  const getInputTypeLabel = (type) =>
    inputTypeOptions.find((option) => option.value === type)?.label || type;

  const handleVideoDelete = () => {
    setUploadedVideo(null);
    setVideoPreview(null);
    setSelectedVideo(null);
  };

  const handleBucketVideoSelect = (videoUrl) => {
    if (videoUrl) {
      setUploadedVideo(videoUrl);
      setVideoPreview(videoUrl);
    }

    setBucketModalVisible(false);
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      streamRef.current.getTracks().forEach((track) => track.stop());

      setIsRecording(false);

      clearInterval(timerRef.current);
    }
  };

  const handleUpload = async (values) => {
    try {
      setUploading(true);

      const videoData = {
        title: values.title,
        description: values.description,
        organization: values.organization,
        interpreter: values.interpreter,
        language: values.language,
        url: pendingCatalogVideo,
        type: "video/mp4",
      };

      await uploadVideo(videoData).unwrap();

      notification.success({
        message: "Success",
        description: "Video uploaded to catalog successfully",
      });

      setUploadModalVisible(false);

      uploadModalForm.resetFields();

      setShowUploadConfirmation(false);

      setPendingCatalogVideo(null);
    } catch (error) {
      notification.error({
        message: "Error",
        description: error.message || "Failed to upload video to catalog",
      });
    } finally {
      setUploading(false);
    }
  };

  const handleUploadToCatalog = () => {
    setShowUploadConfirmation(false);
    setUploadModalVisible(true);
  };

  const handleCloseUploadConfirmation = () => {
    setShowUploadConfirmation(false);
    setPendingCatalogVideo(null);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];

    if (!file) return;

    if (file.size > MAX_QUESTION_VIDEO_UPLOAD_BYTES) {
      notification.error({
        message: "File too large",
        description: `Please upload a video file smaller than ${MAX_QUESTION_VIDEO_UPLOAD_MB} MB`,
      });
      e.target.value = "";
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      console.info("[AddQuestion][Upload] Starting upload", {
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
      });
      setUploadProgress(35);
      const response = await uploadFile({ file }).unwrap();
      setUploadProgress(100);

      if (response?.file) {
        console.info("[AddQuestion][Upload] Upload succeeded", {
          fileUrl: response.file,
        });
        setUploadedVideo(response.file);
        setVideoPreview(response.file);
        setPendingCatalogVideo(response.file);
        setShowUploadConfirmation(true);
        e.target.value = "";

        notification.success({
          message: "Upload Successful",
          description: "Video has been uploaded successfully",
        });
      } else {
        console.error("[AddQuestion][Upload] Missing file URL in response", {
          response,
        });
        throw new Error("Invalid response format");
      }
    } catch (error) {
      console.error("[AddQuestion][Upload] Upload failed", {
        message: error?.message,
        stack: error?.stack,
      });
      notification.error({
        message: "Upload Failed",
        description:
          error?.data?.message ||
          error?.message ||
          `Failed to upload video. Max size is ${MAX_QUESTION_VIDEO_UPLOAD_MB} MB.`,
      });

      e.target.value = "";
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleInputsDragEnd = (result) => {
    if (!result.destination) return;
    if (result.destination.index === result.source.index) return;

    const items = Array.from(inputs);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    setInputs(items);
  };

  const addInput = (type) => {
    const newId = `${Date.now()}`;
    const newInput = {
      id: newId,
      type,
      title: type === "signature" ? "Signature" : "",
      require: false,
      options:
        type === "slider" ||
        type === "dropdown" ||
        type === "checkbox" ||
        type === "radio" ||
        isRadioListInputType(type)
          ? ""
          : undefined,
      rowOptions: isRadioListInputType(type) ? "" : undefined,
      scale_type: type === "slider" ? null : undefined,
      dateType: type === "date" ? "date-range" : undefined,
      fileType: isFileUploadInputType(type) ? "image/*" : undefined,
      recordingLimit: type === "record-video" ? "00:00:30" : undefined,
      show_logos:
        type === "checkbox" || type === "radio" || type === "dropdown"
          ? false
          : undefined,
      option_logos:
        type === "checkbox" || type === "radio" || type === "dropdown"
          ? []
          : undefined,
      conditionEnabled: false,
      conditionSourceId: undefined,
      conditionResponse: undefined,
    };

    setInputs((prevInputs) => [...prevInputs, newInput]);

    mainForm.setFieldsValue({
      [`input_title_${newId}`]: type === "signature" ? "Signature" : "",
      [`required_${newId}`]: false,
    });

    if (type === "slider") {
      mainForm.setFieldsValue({
        [`slider_options_${newId}`]: "",
        [`scale_type_${newId}`]: null,
        [`show_emojis_${newId}`]: false,
        [`show_stars_${newId}`]: false,
        [`show_only_emojis_${newId}`]: false,
      });
    }

    if (type === "checkbox" || type === "radio" || type === "dropdown") {
      mainForm.setFieldsValue({
        [`show_logos_${newId}`]: false,
      });
    }

    setIsAddingNew(false);
  };

  const removeInput = (id) => {
    setInputs((prev) =>
      prev
        .filter((input) => input.id !== id)
        .map((input) =>
          input.conditionSourceId === id
            ? {
                ...input,
                conditionEnabled: false,
                conditionSourceId: undefined,
                conditionResponse: undefined,
              }
            : input,
        ),
    );
  };

  const updateInput = (id, fieldOrPatch, value) => {
    setInputs((prev) =>
      prev.map((input) => {
        if (input.id !== id) return input;

        if (
          fieldOrPatch != null &&
          typeof fieldOrPatch === "object" &&
          !Array.isArray(fieldOrPatch)
        ) {
          return { ...input, ...fieldOrPatch };
        }

        return { ...input, [fieldOrPatch]: value };
      }),
    );
  };

  const handleScaleTypeChange = (inputId, value) => {
    const current = inputs.find((input) => input.id === inputId);
    const nextLabels = parseSliderOptionLabels(current?.options, value);
    const joined = joinSliderOptionLabels(nextLabels);

    updateInput(inputId, "options", joined);
    updateInput(inputId, "scale_type", value);
    mainForm.setFieldValue(`slider_options_${inputId}`, joined);
    mainForm.setFieldValue(`scale_type_${inputId}`, value);

    nextLabels.forEach((label, index) => {
      mainForm.setFieldValue(`slider_option_${inputId}_${index}`, label);
    });
  };

  const handleShowStarsChange = (inputId, checked) => {
    const next = Boolean(checked);
    mainForm.setFieldValue(`show_stars_${inputId}`, next);

    if (next) {
      mainForm.setFieldValue(`show_emojis_${inputId}`, false);
    }

    updateInput(inputId, {
      show_stars: next,
      ...(next ? { show_emojis: false } : {}),
    });
  };

  const handleShowEmojisChange = (inputId, checked) => {
    const next = Boolean(checked);
    mainForm.setFieldValue(`show_emojis_${inputId}`, next);

    if (next) {
      mainForm.setFieldValue(`show_stars_${inputId}`, false);
    }

    updateInput(inputId, {
      show_emojis: next,
      ...(next ? { show_stars: false } : {}),
    });
  };

  const handleShowOnlyEmojisChange = (inputId, checked) => {
    const next = Boolean(checked);
    mainForm.setFieldValue(`show_only_emojis_${inputId}`, next);
    updateInput(inputId, "show_only_emojis", next);
  };

  const handleSliderPointLabelChange = (inputId, pointIndex, value) => {
    const current = inputs.find((input) => input.id === inputId);
    const scaleType =
      mainForm.getFieldValue(`scale_type_${inputId}`) ||
      current?.scale_type ||
      null;
    const labels = parseSliderOptionLabels(current?.options, scaleType);
    labels[pointIndex] = value;
    const joined = joinSliderOptionLabels(labels);

    updateInput(inputId, "options", joined);
    mainForm.setFieldValue(`slider_options_${inputId}`, joined);
    mainForm.setFieldValue(`slider_option_${inputId}_${pointIndex}`, value);
  };

  const handleSubmit = async () => {
    try {
      await mainForm.validateFields();

      const values = mainForm.getFieldsValue();

      for (const input of inputs) {
        if (input.type !== "slider") continue;

        const showOnlyEmojis = toSliderToggleValue(
          mainForm.getFieldValue(`show_only_emojis_${input.id}`) ??
            input.show_only_emojis,
        );
        const scaleType = mainForm.getFieldValue(`scale_type_${input.id}`);
        const validationError = getSliderOptionsValidationError({
          options: input.options,
          scalePoints: scaleType,
          showOnlyEmojis,
          inputTitle: input.title || "Slider",
        });

        if (validationError) {
          notification.error({
            message: "Incomplete slider setup",
            description: validationError,
          });
          return;
        }
      }

      for (const input of inputs) {
        if (
          input.type !== "radio" &&
          input.type !== "checkbox" &&
          input.type !== "dropdown"
        ) {
          continue;
        }

        const showLogos = Boolean(
          mainForm.getFieldValue(`show_logos_${input.id}`) ??
          resolveShowLogosFlag(input),
        );
        const logosError = getChoiceLogosValidationError({
          inputType: input.type,
          options: input.options,
          showLogos,
          optionLogos: input.option_logos,
          inputTitle:
            input.title ||
            (input.type === "radio"
              ? "Radio"
              : input.type === "dropdown"
                ? "Dropdown"
                : "Checkbox"),
        });

        if (logosError) {
          notification.error({
            message: "Incomplete logos setup",
            description: logosError,
          });
          return;
        }
      }

      for (const input of inputs) {
        if (!isRadioListInputType(input.type)) continue;

        const setupError = getRadioListSetupError({
          headers: input.options,
          rows: input.rowOptions,
          inputTitle: input.title || getRadioListTypeLabel(input.type),
        });

        if (setupError) {
          notification.error({
            message: "Incomplete radio list setup",
            description: setupError,
          });
          return;
        }
      }

      for (const input of inputs) {
        if (!input.conditionEnabled) continue;

        const sourceIndex = inputs.findIndex(
          (item) => item.id === input.conditionSourceId,
        );

        if (sourceIndex < 0 || !input.conditionResponse) {
          notification.error({
            message: "Incomplete conditional trigger",
            description: `Set both input and response for "${
              input.title || getInputTypeLabel(input.type)
            }".`,
          });

          return;
        }
      }

      const response = inputs.map((input) => {
        const condition = buildInputConditionPayload({
          enabled: input.conditionEnabled,
          sourceIndex: inputs.findIndex(
            (item) => item.id === input.conditionSourceId,
          ),
          response: input.conditionResponse,
        });

        const baseResponse = {
          input_type: input.type,
          input_title: input.title,
          require: input?.require || false,
          ...(condition ? { condition } : { condition: null }),
        };

        switch (input.type) {
          case "slider":
            const show_only_emojis = toSliderToggleValue(
              mainForm.getFieldValue(`show_only_emojis_${input.id}`) ??
                input.show_only_emojis,
            );
            const show_emojis = toSliderToggleValue(
              mainForm.getFieldValue(`show_emojis_${input.id}`) ??
                input.show_emojis,
            );
            const show_stars = toSliderToggleValue(
              mainForm.getFieldValue(`show_stars_${input.id}`) ??
                input.show_stars,
            );
            const scale_type = mainForm.getFieldValue(`scale_type_${input.id}`);
            const totalOptions = parseInt(scale_type, 10);

            let input_options = [];

            if (show_only_emojis) {
              input_options = Array.from(
                { length: totalOptions },
                (_, i) => `${i}`,
              );
            } else {
              input_options = parseSliderOptionLabels(
                input.options,
                totalOptions,
              );
            }

            return {
              ...baseResponse,
              input_options: input_options,
              scale_type: scale_type,
              show_emojis: show_emojis,
              show_stars: show_stars,
              show_only_emojis: show_only_emojis,
              slider_config: {
                scale_points: totalOptions,
                show_only_emojis: show_only_emojis,
                show_emojis: show_emojis,
                show_stars: show_stars,
                options: input_options,
              },
            };
          case "dropdown":
          case "checkbox":
          case "radio":
            return {
              ...baseResponse,
              ...buildChoiceInputResponseFields(input, mainForm),
            };
          case "checkbox-list":
          case "radio-list":
            return {
              ...baseResponse,
              ...buildRadioListResponseFields(input),
            };
          case "date":
            return {
              ...baseResponse,
              date_type: input.dateType,
            };
          case "file":
          case "multiple-files":
            return {
              ...baseResponse,
              file_type: input.fileType,
            };
          case "record-video":
            return {
              ...baseResponse,
              recording_limit: input.recordingLimit,
            };
          default:
            return baseResponse;
        }
      });

      const showQuestionIndex = toSliderToggleValue(
        useQuestionNumberRedirect ?? values.showQuestionIndex,
      );

      const rawNum = values.questionNumber;
      const numStr =
        rawNum === undefined || rawNum === null ? "" : String(rawNum).trim();
      const finalQuestionNumber =
        numStr === ""
          ? null
          : (() => {
              const parsed = Number(numStr);
              return Number.isInteger(parsed) && parsed >= 0 ? parsed : null;
            })();

      const isConditionalEnabled = Boolean(conditionalEnabled);
      const redirectTo =
        showQuestionIndex && finalQuestionNumber !== null
          ? String(finalQuestionNumber)
          : null;

      const section =
        typeof values.section === "string" ? values.section.trim() : "";

      const questionData = {
        video: uploadedVideo,
        number: finalQuestionNumber,
        title: values.questionTitle,
        description: values.description,
        section: section || null,
        response,
        showTerms: values.showTerms,
        showAnonymous: values.showAnonymous,
        showButton: values.showButton,
        buttonType: values.buttonType,
        showQuestionIndex: showQuestionIndex,
        condition: isConditionalEnabled
          ? [
              {
                trigger: true,
                question: values.conditional_question,
                response: values.conditional_response,
                redirect_to: redirectTo,
              },
            ]
          : null,
      };

      if (mode === "edit") {
        await updateQuestion({
          formId,
          questionId,
          data: questionData,
        }).unwrap();
      } else {
        await createQuestion({ formId, data: questionData }).unwrap();
      }

      navigate(-1);
    } catch (error) {}
  };

  const handleDelete = () => {
    Modal.confirm({
      title: "Delete Question",
      content: "Are you sure you want to delete this question?",
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk: async () => {
        try {
          await deleteQuestion({ formId, questionId }).unwrap();
          navigate(-1);
        } catch (error) {
          notification.error({ message: "Failed to delete question" });
        }
      },
    });
  };

  const getReactEmoji = (index, total) => {
    const total_str = total.toString();

    if (total_str === "4") {
      switch (index) {
        case 0:
          return <Frown size={18} color="#FF4D4F" />;
        case 1:
          return <Meh size={18} color="#FFA940" />;
        case 2:
          return <Smile size={18} color="#52C41A" />;
        case 3:
          return <SmilePlus size={18} color="#1890FF" />;
        default:
          return <SmileIcon size={18} color="#FADB14" />;
      }
    } else if (total_str === "5") {
      switch (index) {
        case 0:
          return <Frown size={18} color="#FF4D4F" />;
        case 1:
          return <Meh size={18} color="#FFA940" />;
        case 2:
          return <SmileIcon size={18} color="#FADB14" />;
        case 3:
          return <Smile size={18} color="#52C41A" />;
        case 4:
          return <SmilePlus size={18} color="#1890FF" />;
        default:
          return <SmileIcon size={18} color="#FADB14" />;
      }
    } else if (total_str === "7") {
      switch (index) {
        case 0:
          return <Frown size={18} color="#FF4D4F" />;
        case 1:
          return <Frown size={18} color="#FF7A45" />;
        case 2:
          return <Meh size={18} color="#FFA940" />;
        case 3:
          return <SmileIcon size={18} color="#FADB14" />;
        case 4:
          return <Smile size={18} color="#73D13D" />;
        case 5:
          return <Smile size={18} color="#52C41A" />;
        case 6:
          return <SmilePlus size={18} color="#1890FF" />;
        default:
          return <SmileIcon size={18} color="#FADB14" />;
      }
    }

    return <SmileIcon size={18} color="#FADB14" />;
  };

  const getReactStar = () => {
    const yellowColor = "#FFD700";

    return <Star size={28} color={yellowColor} fill={yellowColor} />;
  };

  const preventDragHandling = (e) => {
    e.stopPropagation();
  };

  const questionList =
    questionsData?.data?.filter(
      (q) =>
        q._id !== questionId &&
        q.response?.some(
          (r) =>
            (r.input_type === "dropdown" ||
              r.input_type === "radio" ||
              r.input_type === "checkbox") &&
            r.input_options?.length > 0,
        ),
    ) ?? [];

  const responseList = () => {
    const selectedQuestionId = mainForm.getFieldValue("conditional_question");
    const selectedQuestion = questionsData?.data?.find(
      (q) => q._id === selectedQuestionId,
    );

    if (!selectedQuestion) return [];

    const allOptions = selectedQuestion.response?.reduce((acc, r) => {
      if (["dropdown", "radio", "checkbox"].includes(r.input_type)) {
        const raw = r.input_options;
        const normalized = Array.isArray(raw)
          ? raw.map((opt) =>
              typeof opt === "string"
                ? opt
                : String(opt?.label ?? opt?.value ?? opt ?? "").trim(),
            )
          : raw;
        const options = parseChoiceOptionLines(normalized);

        return [...acc, ...options];
      }

      return acc;
    }, []);

    return Array.isArray(allOptions) ? [...new Set(allOptions)] : [];
  };

  const displayedItems = (input) => {
    const scaleType = mainForm.getFieldValue(`scale_type_${input.id}`);
    const showOnlyEmojis = toSliderToggleValue(
      mainForm.getFieldValue(`show_only_emojis_${input.id}`) ??
        input.show_only_emojis,
    );
    const showEmojis = toSliderToggleValue(
      mainForm.getFieldValue(`show_emojis_${input.id}`) ?? input.show_emojis,
    );
    const showStars = toSliderToggleValue(
      mainForm.getFieldValue(`show_stars_${input.id}`) ?? input.show_stars,
    );
    const numberOfPoints = parseInt(scaleType, 10);

    if (!Number.isInteger(numberOfPoints) || numberOfPoints < 1) {
      return {
        displayItems: [],
        showEmojis,
        showStars,
        showOnlyEmojis,
        numberOfPoints: 0,
      };
    }

    let displayItems = [];

    if (showOnlyEmojis) {
      displayItems = Array(numberOfPoints).fill("");
    } else {
      displayItems = parseSliderOptionLabels(input.options, numberOfPoints);
    }

    return {
      displayItems,
      showEmojis,
      showStars,
      showOnlyEmojis,
      numberOfPoints,
    };
  };

  useEffect(() => {
    const timer = setTimeout(() => setIsPageReady(true), 160);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (mode === "edit" && questionData?.data) {
      const data = questionData.data;
      const formValues = {
        questionNumber: data.number,
        questionTitle: data.title,
        description: data.description,
        section: data.section || "",
        showButton: data.showButton ?? true,
        buttonType: data.buttonType || (data.number === 0 ? "start" : "next"),
        showTerms: data.showTerms ?? false,
        showAnonymous: data.showAnonymous ?? false,
        showQuestionIndex: toSliderToggleValue(data.showQuestionIndex),
      };

      if (data.condition && data.condition.length > 0) {
        const condition = data.condition[0];
        setConditionalEnabled(true);
        const initialUseQuestionNumber =
          data.showQuestionIndex == null
            ? condition.redirect_to != null
            : toSliderToggleValue(data.showQuestionIndex);
        setUseQuestionNumberRedirect(initialUseQuestionNumber);
        formValues.showQuestionIndex = initialUseQuestionNumber;

        const conditionQuestionId = normalizeConditionId(condition.question);

        formValues.conditional_question = conditionQuestionId || undefined;
        formValues.conditional_response = condition.response;
      } else {
        setConditionalEnabled(false);
        setUseQuestionNumberRedirect(toSliderToggleValue(data.showQuestionIndex));
      }

      if (data.video) {
        setUploadedVideo(data.video);
        setVideoPreview(data.video);
      } else {
        setUploadedVideo(null);
        setVideoPreview(null);
      }

      if (data.response && Array.isArray(data.response)) {
        const formattedInputs = data.response.map((input, index) => {
          const inputId = `${Date.now()}-${index}`;
          formValues[`input_title_${inputId}`] = input.input_title;
          formValues[`required_${inputId}`] = input.require;

          if (input.input_type === "slider") {
            const scaleType = resolveSliderScaleType(
              input.scale_type,
              input.input_options,
            );
            const optionLabels = parseSliderOptionLabels(
              input.input_options,
              scaleType,
            );
            const joinedOptions = joinSliderOptionLabels(optionLabels);

            const sliderFlags = resolveSliderToggleFlags(input);
            formValues[`slider_options_${inputId}`] = joinedOptions;
            formValues[`scale_type_${inputId}`] = scaleType;
            formValues[`show_emojis_${inputId}`] = sliderFlags.show_emojis;
            formValues[`show_stars_${inputId}`] = sliderFlags.show_stars;
            formValues[`show_only_emojis_${inputId}`] =
              sliderFlags.show_only_emojis;

            optionLabels.forEach((label, pointIndex) => {
              formValues[`slider_option_${inputId}_${pointIndex}`] = label;
            });
          }

          if (
            input.input_type === "checkbox" ||
            input.input_type === "radio" ||
            input.input_type === "dropdown"
          ) {
            const showLogos = resolveShowLogosFlag(input);
            formValues[`show_logos_${inputId}`] = showLogos;
          }

          const inputCondition = getInputCondition(input);

          return {
            id: inputId,
            type: input.input_type,
            title: input.input_title,
            require: input.require,
            options: input.input_options
              ? input.input_type === "slider"
                ? joinSliderOptionLabels(
                    parseSliderOptionLabels(
                      input.input_options,
                      resolveSliderScaleType(
                        input.scale_type,
                        input.input_options,
                      ),
                    ),
                  )
                : ["dropdown", "checkbox", "radio"].includes(input.input_type)
                  ? Array.isArray(input.input_options)
                    ? input.input_options.join("\n")
                    : input.input_options
                  : input.input_options
              : "",
            ...(isRadioListInputType(input.input_type)
              ? getRadioListEditorState(input)
              : {}),
            ...resolveSliderToggleFlags(input),
            scale_type: resolveSliderScaleType(
              input.scale_type,
              input.input_options,
            ),
            dateType: input.date_type || "date-range",
            fileType: input.file_type || "image/*",
            recordingLimit: input.recording_limit || "00:00:00",
            show_logos: resolveShowLogosFlag(input),
            option_logos: Array.isArray(input.option_logos)
              ? input.option_logos
              : [],
            conditionEnabled: Boolean(inputCondition?.trigger),
            conditionSourceId: undefined,
            conditionResponse: inputCondition?.response,
            _conditionSourceIndex:
              inputCondition?.input_index !== undefined
                ? Number(inputCondition.input_index)
                : undefined,
          };
        });

        const withResolvedSources = formattedInputs.map((item) => {
          if (
            item._conditionSourceIndex === undefined ||
            Number.isNaN(item._conditionSourceIndex)
          ) {
            const { _conditionSourceIndex, ...rest } = item;
            return rest;
          }

          const source = formattedInputs[item._conditionSourceIndex];
          const { _conditionSourceIndex, ...rest } = item;
          return {
            ...rest,
            conditionSourceId: source?.id,
            conditionEnabled: Boolean(source?.id) && rest.conditionEnabled,
          };
        });

        setInputs(withResolvedSources);
      }

      mainForm.setFieldsValue(formValues);
    } else if (mode !== "edit" && !isQuestionsListLoading) {
      setConditionalEnabled(false);
      setUseQuestionNumberRedirect(false);
      setUploadedVideo(null);
      setVideoPreview(null);

      mainForm.setFieldsValue({
        showButton: true,
        buttonType: nextNumber === 0 ? "start" : "next",
        showQuestionIndex: false,
        questionNumber: nextNumber,
      });
    }
  }, [questionData, mode, mainForm, nextNumber, isQuestionsListLoading]);

  return (
    <Form
      form={mainForm}
      layout="vertical"
      onFinish={handleSubmit}
      className="space-y-6 bg-white m-0"
    >
      <QuestionPageHeader
        mode={mode}
        onSubmit={handleSubmit}
        onDelete={handleDelete}
      />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-0">
        <HeaderActions
          videoPreview={videoPreview}
          isVideoLoading={mode === "edit" && isQuestionLoading}
          isUploading={isUploading}
          handleVideoDelete={handleVideoDelete}
          handleFileUpload={handleFileUpload}
          setVideoModalVisible={setVideoModalVisible}
          setBucketModalVisible={setBucketModalVisible}
          maxVideoUploadMb={MAX_QUESTION_VIDEO_UPLOAD_MB}
        />
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Form.Item
              layout="vertical"
              label="Question #"
              className="mb-0"
              name="questionNumber"
              dependencies={["showQuestionIndex"]}
              rules={[
                ({ getFieldValue }) => ({
                  validator: (_, value) => {
                    const showIdx = getFieldValue("showQuestionIndex");
                    const str =
                      value === undefined || value === null
                        ? ""
                        : String(value).trim();

                    if (showIdx === false && str === "") {
                      return Promise.reject(
                        new Error("Question number is required"),
                      );
                    }

                    if (str === "") {
                      return Promise.resolve();
                    }

                    const parsed = Number(str);

                    if (!Number.isInteger(parsed) || parsed < 0) {
                      return Promise.reject(
                        new Error("Question number must be 0 or greater"),
                      );
                    }

                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <Input type="number" min={0} className="h-11 rounded-xl" />
            </Form.Item>
            <Form.Item
              layout="vertical"
              label="Question Title"
              className="mb-0"
              name="questionTitle"
              rules={[
                { required: true, message: "Question title is required" },
              ]}
            >
              <Input placeholder="Type here..." className="h-11 rounded-xl" />
            </Form.Item>
          </div>
          <Form.Item
            layout="vertical"
            name="description"
            label="Description"
            className="mb-0"
          >
            <Input.TextArea
              size="large"
              placeholder="Type here..."
              className="rounded-xl"
              rows={4}
            />
          </Form.Item>
        </div>
      </div>
      <Form.Item>
        <div
          className={`flex flex-row items-start gap-8 transition-all duration-300 ease-out ${
            isPageReady
              ? "opacity-100 translate-y-0"
              : "opacity-0 -translate-y-1"
          }`}
        >
          {showFirstQuestionOptions && (
            <div className="flex flex-col items-start gap-2 text-left w-1/2">
              <Form.Item
                name="showTerms"
                valuePropName="checked"
                initialValue={false}
                getValueProps={(value) => ({
                  checked: value === undefined ? false : value,
                })}
                noStyle
              >
                <Checkbox>Show Terms & Conditions checkbox</Checkbox>
              </Form.Item>
              <Form.Item
                name="showAnonymous"
                valuePropName="checked"
                initialValue={false}
                getValueProps={(value) => ({
                  checked: value === undefined ? false : value,
                })}
                noStyle
              >
                <Checkbox>Show Anonymous Response option</Checkbox>
              </Form.Item>
            </div>
          )}
          <div className="flex flex-col items-start gap-2 text-left w-1/2">
            <label className="flex flex-row items-center gap-2">
              <Switch
                className="custom-switch custom-switch--compact"
                checked={useQuestionNumberRedirect}
                onChange={(checked) => {
                  setUseQuestionNumberRedirect(checked);
                  mainForm.setFieldValue("showQuestionIndex", checked);
                }}
              />
              Use Question Number
            </label>

            <Form.Item name="showQuestionIndex" noStyle>
              <Input type="hidden" />
            </Form.Item>
            <label className="flex flex-row items-center gap-2">
              <Switch
                className="custom-switch custom-switch--compact"
                checked={conditionalEnabled}
                onChange={(checked) => {
                  setConditionalEnabled(checked);

                  if (!checked) {
                    mainForm.setFieldsValue({
                      conditional_question: undefined,
                      conditional_response: undefined,
                    });
                  }
                }}
              />
              Conditional Trigger
            </label>
          </div>
        </div>
      </Form.Item>
      <div>
        <div className="border-top border-gray-200">
          <div className="grid grid-cols-1 gap-4 test">
            {conditionalEnabled && (
              <div className="flex flex-row gap-6 flex-wrap">
                <Form.Item
                  name="conditional_question"
                  label="Question"
                  className="mb-0 w-[calc(50%-12px)]"
                >
                  <Select
                    size="large"
                    placeholder="Select--"
                    className="w-full"
                    onChange={(value) => {
                      mainForm.setFieldsValue({
                        conditional_response: undefined,
                      });

                      setInputs([...inputs]);
                    }}
                  >
                    {questionList.map((question) => (
                      <Option key={question._id} value={question._id}>
                        {`Question ${question.number}: ${question.title}`}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item
                  name="conditional_response"
                  label="Response"
                  className="mb-0 w-[calc(50%-12px)]"
                >
                  <Select
                    size="large"
                    placeholder="Select Response"
                    className="w-full"
                    disabled={!mainForm.getFieldValue("conditional_question")}
                  >
                    {responseList().map((option, index) => (
                      <Option key={`${index}-${option}`} value={option}>
                        {option}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </div>
            )}
          </div>
        </div>
        <div className="mt-4">
          <Form.Item
            name="section"
            label="Section"
            className="mb-4"
          >
            <Input
              size="large"
              placeholder="e.g. About you, Feedback, Demographics"
              className="rounded-xl"
            />
          </Form.Item>
          <QuestionButtonConfig
            mainForm={mainForm}
            questionNumber={questionNumber}
            buttonOptions={buttonOptions}
          />
        </div>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-md font-semibold m-0">Response Input</h3>
          <div className="relative">
            <Button
              type="primary"
              onClick={() => setIsAddingNew(!isAddingNew)}
              className="text-white border-0 px-6 py-2 h-11 rounded-xl text-sm font-medium"
            >
              + Add Input Field
            </Button>
            {isAddingNew && (
              <div className="absolute right-0 top-full mt-2 bg-white rounded-2xl shadow-lg border border-gray-100 w-48 py-2 z-50 max-h-64 overflow-y-auto">
                {inputTypeOptions.map((option) => (
                  <Button
                    key={option.value}
                    type="text"
                    block
                    className="text-left hover:bg-gray-50 h-9"
                    onClick={() => addInput(option.value)}
                  >
                    {option.label}
                  </Button>
                ))}
              </div>
            )}
          </div>
        </div>
        <div
          className={`transition-opacity duration-300 ease-out ${
            isPageReady ? "opacity-100" : "opacity-0"
          }`}
        >
          <DragDropContext onDragEnd={handleInputsDragEnd}>
            <Droppable
              droppableId="question-inputs-list"
              type="QUESTION_INPUTS"
              direction="vertical"
            >
              {(droppableProvided) => (
                <div
                  ref={droppableProvided.innerRef}
                  {...droppableProvided.droppableProps}
                  className="space-y-4"
                >
            {inputs.map((input, index) => (
              <Draggable
                key={input.id}
                draggableId={String(input.id)}
                index={index}
              >
                {(dragProvided) => (
              <div
                ref={dragProvided.innerRef}
                {...dragProvided.draggableProps}
                style={lockVerticalDragStyle(dragProvided.draggableProps.style)}
                className="bg-white rounded-2xl border border-gray-200 p-6"
              >
                <div className="flex items-start gap-6 ">
                  <div className="flex flex-col items-center gap-4 ">
                    <CloseOutlined
                      className="text-red-500"
                      onClick={() => removeInput(input.id)}
                    />
                    <p className="text-gray-400">{index + 1}</p>
                    <div
                      {...dragProvided.dragHandleProps}
                      className="cursor-grab touch-none text-black hover:text-gray-600 active:cursor-grabbing"
                      aria-label="Drag to reorder"
                    >
                      <MenuOutlined className="rotate-90" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="flex gap-4 w-full flex-wrap">
                      <Form.Item
                        layout="vertical"
                        label={`${getInputTypeLabel(input.type)} Title`}
                        className="mb-0 flex-1"
                        name={`input_title_${input.id}`}
                      >
                        <Input
                          size="large"
                          value={input.title}
                          onChange={(e) => {
                            updateInput(input.id, "title", e.target.value);

                            mainForm.setFieldValue(
                              `input_title_${input.id}`,
                              e.target.value,
                            );
                          }}
                          placeholder={
                            input.type === "slider"
                              ? "Title (Optional)"
                              : "Type here..."
                          }
                        />
                      </Form.Item>
                      <Form.Item
                        label="Required"
                        className="mb-0"
                        valuePropName="checked"
                        name={`required_${input.id}`}
                      >
                        <Switch
                          className="custom-switch custom-switch--compact"
                          checked={input.require}
                          onChange={(checked) => {
                            updateInput(input.id, "require", checked);

                            mainForm.setFieldValue(
                              `required_${input.id}`,
                              checked,
                            );
                          }}
                        />
                      </Form.Item>
                      {input.type === "slider" && (
                        <div className="space-y-4 w-full">
                          <Form.Item
                            layout="vertical"
                            label="Scale Type"
                            className="mb-0"
                            name={`scale_type_${input.id}`}
                            rules={[
                              {
                                required: true,
                                message: "Please select a Scale Type",
                              },
                            ]}
                          >
                            <Select
                              allowClear
                              placeholder="Select scale type"
                              value={
                                mainForm.getFieldValue(
                                  `scale_type_${input.id}`,
                                ) ?? undefined
                              }
                              onChange={(value) =>
                                handleScaleTypeChange(input.id, value ?? null)
                              }
                              className="w-full rounded-xl"
                              dropdownRender={(menu) => (
                                <div onMouseDown={preventDragHandling}>
                                  {menu}
                                </div>
                              )}
                            >
                              {getSliderScaleSelectOptions(
                                mainForm.getFieldValue(
                                  `scale_type_${input.id}`,
                                ) ?? input.scale_type,
                              ).map((option) => (
                                <Option key={option.value} value={option.value}>
                                  {option.label}
                                </Option>
                              ))}
                            </Select>
                          </Form.Item>
                          <div className="flex flex-row gap-8">
                            <label className="flex items-center gap-2">
                              <Form.Item
                                className="mb-0"
                                valuePropName="checked"
                                name={`show_emojis_${input.id}`}
                                noStyle
                              >
                                <Switch
                                  className="custom-switch custom-switch--compact"
                                  checked={Boolean(input.show_emojis)}
                                  onChange={(checked) =>
                                    handleShowEmojisChange(input.id, checked)
                                  }
                                />
                              </Form.Item>
                              Show Emojis
                            </label>
                            <label className="flex items-center gap-2">
                              <Form.Item
                                className="mb-0"
                                valuePropName="checked"
                                name={`show_stars_${input.id}`}
                                noStyle
                              >
                                <Switch
                                  className="custom-switch custom-switch--compact"
                                  checked={Boolean(input.show_stars)}
                                  onChange={(checked) =>
                                    handleShowStarsChange(input.id, checked)
                                  }
                                />
                              </Form.Item>
                              Show Stars
                            </label>
                            <label className="flex items-center gap-2">
                              <Form.Item
                                className="mb-0"
                                valuePropName="checked"
                                name={`show_only_emojis_${input.id}`}
                                noStyle
                              >
                                <Switch
                                  className="custom-switch custom-switch--compact"
                                  checked={Boolean(input.show_only_emojis)}
                                  onChange={(checked) =>
                                    handleShowOnlyEmojisChange(
                                      input.id,
                                      checked,
                                    )
                                  }
                                />
                              </Form.Item>
                              No Text
                            </label>
                          </div>
                          {!input.show_only_emojis &&
                            (mainForm.getFieldValue(
                              `scale_type_${input.id}`,
                            ) ||
                              input.scale_type) && (
                              <div className="space-y-3 w-full">
                                <div className="text-sm font-medium text-gray-700">
                                  Scale point labels (left to right)
                                </div>
                                <Form.Item
                                  name={`slider_options_${input.id}`}
                                  hidden
                                >
                                  <Input type="hidden" />
                                </Form.Item>
                                {parseSliderOptionLabels(
                                  input.options,
                                  mainForm.getFieldValue(
                                    `scale_type_${input.id}`,
                                  ),
                                ).map((label, pointIndex, labels) => (
                                  <Form.Item
                                    key={`slider_option_${input.id}_${pointIndex}`}
                                    layout="vertical"
                                    className="mb-0"
                                    label={`Point ${pointIndex + 1}${
                                      pointIndex === 0
                                        ? " (left)"
                                        : pointIndex === labels.length - 1
                                          ? " (right)"
                                          : ""
                                    }`}
                                    name={`slider_option_${input.id}_${pointIndex}`}
                                    rules={[
                                      {
                                        validator: async (_, value) => {
                                          if (
                                            mainForm.getFieldValue(
                                              `show_only_emojis_${input.id}`,
                                            )
                                          ) {
                                            return Promise.resolve();
                                          }

                                          if (
                                            !mainForm.getFieldValue(
                                              `scale_type_${input.id}`,
                                            )
                                          ) {
                                            return Promise.resolve();
                                          }

                                          if (
                                            isMissingSliderOptionLabel(value)
                                          ) {
                                            throw new Error(
                                              "Enter a label for this scale point",
                                            );
                                          }

                                          return Promise.resolve();
                                        },
                                      },
                                    ]}
                                  >
                                    <Input
                                      size="large"
                                      className="rounded-xl"
                                      placeholder={`Label for point ${pointIndex + 1}`}
                                      value={label}
                                      onChange={(e) =>
                                        handleSliderPointLabelChange(
                                          input.id,
                                          pointIndex,
                                          e.target.value,
                                        )
                                      }
                                    />
                                  </Form.Item>
                                ))}
                              </div>
                            )}
                          <div className="border border-gray-200 mt-4 p-4 bg-gray-50 rounded-xl">
                            <div className="flex justify-between items-center">
                              {(() => {
                                const preview = displayedItems(input);

                                if (
                                  !preview.numberOfPoints ||
                                  !preview.displayItems?.length
                                ) {
                                  return (
                                    <span className="text-sm text-gray-500">
                                      Select a Scale Type to preview
                                    </span>
                                  );
                                }

                                return preview.displayItems.map(
                                  (option, idx) => (
                                    <div
                                      key={idx}
                                      className="flex flex-col items-center"
                                    >
                                      {(preview.showEmojis ||
                                        preview.showOnlyEmojis ||
                                        preview.showStars) && (
                                        <>
                                          {preview.showStars
                                            ? getReactStar()
                                            : getReactEmoji(
                                                idx,
                                                preview.numberOfPoints,
                                              )}
                                        </>
                                      )}
                                      {!preview.showOnlyEmojis && (
                                        <span className="text-sm mt-1 text-center px-1">
                                          {String(option ?? "").trim() ||
                                            `Point ${idx + 1}`}
                                        </span>
                                      )}
                                    </div>
                                  ),
                                );
                              })()}
                            </div>
                          </div>
                        </div>
                      )}
                      {(input.type === "checkbox" ||
                        input.type === "radio" ||
                        input.type === "dropdown") && (
                        <Form.Item layout="vertical" className="mb-0 flex-1">
                          <ChoiceOptionsEditor
                            input={input}
                            updateInput={updateInput}
                            mainForm={mainForm}
                            uploadFile={uploadFile}
                            preventDragHandling={preventDragHandling}
                            optionLabel={
                              input.type === "checkbox"
                                ? "Checkbox Options"
                                : input.type === "radio"
                                  ? "Radio Options"
                                  : "Drop Down Options"
                            }
                          />
                        </Form.Item>
                      )}
                      {isRadioListInputType(input.type) && (
                        <RadioListEditor
                          input={input}
                          updateInput={updateInput}
                        />
                      )}
                      {input.type === "date" && (
                        <Form.Item
                          layout="vertical"
                          label="Date Type"
                          className="mb-0 flex-1"
                        >
                          <Select
                            size="large"
                            value={input.dateType}
                            onChange={(value) =>
                              updateInput(input.id, "dateType", value)
                            }
                            className="w-full"
                            dropdownRender={(menu) => (
                              <div onMouseDown={preventDragHandling}>
                                {menu}
                              </div>
                            )}
                          >
                            {dateTypes.map((option) => (
                              <Option key={option.value} value={option.value}>
                                {option.label}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                      )}
                      {isFileUploadInputType(input.type) && (
                        <Form.Item
                          layout="vertical"
                          label="File Type"
                          className="mb-0 flex-1"
                        >
                          <Select
                            size="large"
                            value={input.fileType}
                            onChange={(value) =>
                              updateInput(input.id, "fileType", value)
                            }
                            className="w-full"
                            dropdownRender={(menu) => (
                              <div onMouseDown={preventDragHandling}>
                                {menu}
                              </div>
                            )}
                          >
                            {fileTypes.map((option) => (
                              <Option key={option.value} value={option.value}>
                                {option.label}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                      )}
                      {input.type === "record-video" && (
                        <Form.Item
                          layout="vertical"
                          label="Recording Limit Duration"
                          className="mb-0 flex-1"
                          rules={[
                            {
                              required: true,
                              message: "Please select a recording duration",
                            },
                          ]}
                        >
                          <input
                            type="time"
                            step="1"
                            className="ant-input ant-input-lg w-full h-11 rounded-2xl border border-gray-700 px-2"
                            value={input.recordingLimit || "00:00:00"}
                            onChange={(e) => {
                              const timeValue = e.target.value;
                              updateInput(
                                input.id,
                                "recordingLimit",
                                timeValue,
                              );
                            }}
                          />
                          <div className="text-xs text-gray-500 mt-1">
                            Format: HH:MM:SS (e.g., 00:02:30 for 2 minutes 30
                            seconds)
                          </div>
                        </Form.Item>
                      )}
                      {inputs.some(
                        (candidate) =>
                          candidate.id !== input.id &&
                          isChoiceInputType(candidate.type),
                      ) && (
                        <div className="w-full space-y-3 border-t border-gray-100 pt-4">
                          <label className="flex flex-row items-center gap-2">
                            <Switch
                              className="custom-switch custom-switch--compact"
                              checked={Boolean(input.conditionEnabled)}
                              onChange={(checked) => {
                                updateInput(input.id, {
                                  conditionEnabled: checked,
                                  ...(checked
                                    ? {}
                                    : {
                                        conditionSourceId: undefined,
                                        conditionResponse: undefined,
                                      }),
                                });
                              }}
                            />
                            Conditional Trigger
                          </label>
                          {input.conditionEnabled && (
                            <div className="flex w-full flex-col gap-3 sm:flex-row">
                              <Form.Item
                                label="Input"
                                className="mb-0 w-full sm:flex-1"
                              >
                                <Select
                                  size="large"
                                  placeholder="Select input"
                                  className="w-full"
                                  value={input.conditionSourceId}
                                  onChange={(value) => {
                                    updateInput(input.id, {
                                      conditionSourceId: value,
                                      conditionResponse: undefined,
                                    });
                                  }}
                                >
                                  {inputs
                                    .filter(
                                      (candidate) =>
                                        candidate.id !== input.id &&
                                        isChoiceInputType(candidate.type),
                                    )
                                    .map((candidate) => (
                                      <Option
                                        key={candidate.id}
                                        value={candidate.id}
                                      >
                                        {candidate.title ||
                                          getInputTypeLabel(candidate.type)}
                                      </Option>
                                    ))}
                                </Select>
                              </Form.Item>
                              <Form.Item
                                label="Response"
                                className="mb-0 w-full sm:flex-1"
                              >
                                <Select
                                  size="large"
                                  placeholder="Select response"
                                  className="w-full"
                                  disabled={!input.conditionSourceId}
                                  value={input.conditionResponse}
                                  onChange={(value) =>
                                    updateInput(
                                      input.id,
                                      "conditionResponse",
                                      value,
                                    )
                                  }
                                >
                                  {parseChoiceOptionLines(
                                    inputs.find(
                                      (candidate) =>
                                        candidate.id ===
                                        input.conditionSourceId,
                                    )?.options,
                                  ).map((option, optionIndex) => (
                                    <Option
                                      key={`${option}-${optionIndex}`}
                                      value={option}
                                    >
                                      {option}
                                    </Option>
                                  ))}
                                </Select>
                              </Form.Item>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
                )}
              </Draggable>
            ))}
                  {droppableProvided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </div>
      </div>
      <VideoModal
        open={videoModalVisible}
        isRecording={isRecording}
        videoRef={videoRef}
        streamRef={streamRef}
        stopRecording={stopRecording}
        setVideoModalVisible={setVideoModalVisible}
        setVideoPreview={setVideoPreview}
        setUploadedVideo={setUploadedVideo}
      />
      <UploadProgressOverlay
        isUploading={isUploading}
        uploadProgress={uploadProgress}
      />
      <VideoBucketModal
        open={bucketModalVisible}
        onCancel={() => setBucketModalVisible(false)}
        videos={videosData?.data?.videoCatalog || []}
        onSelect={handleBucketVideoSelect}
        selectedVideoUrl={uploadedVideo}
        selectedVideo={selectedVideo}
        setSelectedVideo={setSelectedVideo}
        organizations={orgsData?.data || []}
        interpreters={interpretersData?.data || []}
        onFiltersChange={setVideoFilters}
      />
      <UploadConfirmationModal
        visible={showUploadConfirmation}
        onClose={handleCloseUploadConfirmation}
        onUploadToCatalog={handleUploadToCatalog}
        videoPreview={pendingCatalogVideo}
      />
      <Modal
        title="Upload Video"
        open={uploadModalVisible}
        onCancel={() => {
          setUploadModalVisible(false);
          uploadModalForm.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={uploadModalForm}
          layout="vertical"
          onFinish={handleUpload}
          className="mt-4"
        >
          <Form.Item
            name="title"
            label="Video Title"
            rules={[{ required: true, message: "Please enter video title" }]}
          >
            <Input placeholder="Type here..." className="rounded-xl" />
          </Form.Item>
          <Form.Item
            name="language"
            label="Sign Language"
            className="w-full"
            rules={[
              { required: true, message: "Please select a sign language" },
            ]}
          >
            <Select placeholder="Select language" className="rounded-xl h-11">
              {languageOptions.map((option) => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="description"
            label="Video Description"
            rules={[
              { required: true, message: "Please enter video description" },
            ]}
          >
            <Input.TextArea
              placeholder="Type here..."
              rows={4}
              className="rounded-xl"
            />
          </Form.Item>
          <div className="flex gap-2">
            <Form.Item
              name="interpreter"
              className="w-full"
              label="Presenter / Interpreter"
              rules={[{ required: true, message: "Please select a presenter" }]}
            >
              <Select placeholder="Select--" className="rounded-xl h-11">
                {interpretersData?.data
                  ?.filter(
                    (interpreter) =>
                      interpreter.status?.toLowerCase() !== "inactive",
                  )
                  ?.map((interpreter) => (
                    <Option key={interpreter._id} value={interpreter._id}>
                      {interpreter?.user?.full_name || "Unnamed Interpreter"}
                    </Option>
                  ))}
              </Select>
            </Form.Item>
            <Form.Item
              name="organization"
              className="w-full"
              label="Associated Organisation (Optional)"
            >
              <Select placeholder="Select--" className="rounded-xl h-11">
                {orgsData?.data?.map((org) => (
                  <Option key={org._id} value={org._id}>
                    {org.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </div>
          <div className="mb-4">
            <div className="font-medium mb-2">Video Preview</div>
            <video
              src={pendingCatalogVideo}
              className="w-full rounded-xl"
              style={{ maxHeight: "200px" }}
              autoPlay
              muted
              loop
              playsInline
            />
          </div>
          <Form.Item className="mb-0">
            <Button
              type="primary"
              htmlType="submit"
              loading={uploading}
              className="w-full h-12 rounded-xl bg-[#18181B]"
            >
              Upload to Catalogue
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </Form>
  );
};

export default AddQuestion;
